/* import __COLOCATED_TEMPLATE__ from './editor-modal.hbs'; */
/* RESPONSIBLE TEAM: team-data-foundations */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { dropTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import type { TaskGenerator } from 'ember-concurrency';
import type NotificationsService from 'embercom/services/notifications-service';
import type IntlService from 'embercom/services/intl';
import type AppService from 'embercom/services/app-service';
import { get, post, put } from 'embercom/lib/ajax';

interface ChannelIdentityRule {
  id?: number;
  channel: string | null;
  channel_display_name?: string;
  audience: string | null;
  verification_method: string;
  fallback_rule: boolean;
}

interface Signature {
  Element: HTMLDivElement;
  Args: {
    ruleId?: number;
    onClose: () => void;
    onSave: () => void;
  };
}

const MESSENGER_SECURITY_CHANNELS = ['web', 'ios', 'android'];

export default class EditorModal extends Component<Signature> {
  @service declare notificationsService: InstanceType<typeof NotificationsService>;
  @service declare intl: IntlService;
  @service declare appService: AppService;

  @tracked rule: ChannelIdentityRule | null = null;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    if (args.ruleId) {
      taskFor(this.loadRule).perform(args.ruleId);
    } else {
      this.rule = {
        channel: null,
        audience: null,
        verification_method: 'no_verification',
        fallback_rule: false,
      };
    }
  }

  get isLoading(): boolean {
    return taskFor(this.loadRule).isRunning;
  }

  get isNew(): boolean {
    return !this.rule?.id;
  }

  get isFallback(): boolean {
    return this.rule?.fallback_rule || false;
  }

  get title(): string {
    if (this.isFallback) {
      return this.isNew
        ? this.intl.t('new-settings.workspace.channel-identity-rules.modal.add-fallback-title')
        : this.intl.t('new-settings.workspace.channel-identity-rules.modal.edit-fallback-title');
    }
    return this.isNew
      ? this.intl.t('new-settings.workspace.channel-identity-rules.modal.add-title')
      : this.intl.t('new-settings.workspace.channel-identity-rules.modal.edit-title');
  }

  get channels() {
    return [
      {
        text: this.intl.t('new-settings.workspace.channel-identity-rules.channels.web'),
        value: 'web',
      },
      {
        text: this.intl.t('new-settings.workspace.channel-identity-rules.channels.ios'),
        value: 'ios',
      },
      {
        text: this.intl.t('new-settings.workspace.channel-identity-rules.channels.android'),
        value: 'android',
      },
      {
        text: this.intl.t('new-settings.workspace.channel-identity-rules.channels.email'),
        value: 'email',
      },
      {
        text: this.intl.t('new-settings.workspace.channel-identity-rules.channels.whatsapp'),
        value: 'whatsapp',
      },
      {
        text: this.intl.t('new-settings.workspace.channel-identity-rules.channels.facebook'),
        value: 'facebook',
      },
      {
        text: this.intl.t('new-settings.workspace.channel-identity-rules.channels.instagram'),
        value: 'instagram',
      },
      {
        text: this.intl.t('new-settings.workspace.channel-identity-rules.channels.sms'),
        value: 'sms',
      },
      {
        text: this.intl.t('new-settings.workspace.channel-identity-rules.channels.twitter'),
        value: 'twitter',
      },
      {
        text: this.intl.t('new-settings.workspace.channel-identity-rules.channels.phone-call'),
        value: 'phone_call',
      },
      {
        text: this.intl.t('new-settings.workspace.channel-identity-rules.channels.slack'),
        value: 'slack',
      },
      {
        text: this.intl.t('new-settings.workspace.channel-identity-rules.channels.discord'),
        value: 'discord',
      },
    ];
  }

  get audiences() {
    let audience = this.rule?.audience;
    return [
      {
        text: this.intl.t('new-settings.workspace.channel-identity-rules.audiences.users'),
        value: 'users',
        isSelected: audience === 'users_and_leads' || audience === 'users_only',
      },
      {
        text: this.intl.t('new-settings.workspace.channel-identity-rules.audiences.leads'),
        value: 'leads',
        isSelected: audience === 'users_and_leads' || audience === 'leads_only',
      },
    ];
  }

  get audienceLabel(): string {
    let audience = this.rule?.audience;
    if (audience === 'users_and_leads') {
      return this.intl.t('new-settings.workspace.channel-identity-rules.audiences.users-and-leads');
    } else if (audience === 'users_only') {
      return this.intl.t('new-settings.workspace.channel-identity-rules.audiences.users');
    } else if (audience === 'leads_only') {
      return this.intl.t('new-settings.workspace.channel-identity-rules.audiences.leads');
    }
    return this.intl.t('new-settings.workspace.channel-identity-rules.modal.select-audience');
  }

  get verificationMethods() {
    return [
      {
        text: this.intl.t(
          'new-settings.workspace.channel-identity-rules.verification-methods.no-verification',
        ),
        value: 'no_verification',
      },
      {
        text: this.intl.t(
          'new-settings.workspace.channel-identity-rules.verification-methods.email-otp',
        ),
        description: this.intl.t(
          'new-settings.workspace.channel-identity-rules.verification-methods.email-otp-hint',
        ),
        value: 'email_otp',
      },
      {
        text: this.intl.t(
          'new-settings.workspace.channel-identity-rules.verification-methods.messenger-security',
        ),
        description: this.intl.t(
          'new-settings.workspace.channel-identity-rules.verification-methods.messenger-security-hint',
        ),
        value: 'messenger_security',
      },
    ];
  }

  get selectedChannel(): string | undefined {
    return this.rule?.channel || undefined;
  }

  @action
  setChannel(value: string): void {
    if (this.rule) {
      this.rule = { ...this.rule, channel: value };
    }
  }

  @action
  onAudienceSelectionChange(selectedValues: string[]): void {
    if (!this.rule) {
      return;
    }
    let hasUsers = selectedValues.includes('users');
    let hasLeads = selectedValues.includes('leads');
    let audience: string;
    if (hasUsers && hasLeads) {
      audience = 'users_and_leads';
    } else if (hasUsers) {
      audience = 'users_only';
    } else if (hasLeads) {
      audience = 'leads_only';
    } else {
      this.rule = { ...this.rule, audience: null };
      return;
    }
    this.rule = { ...this.rule, audience };
  }

  @action
  setVerificationMethod(value: string): void {
    if (this.rule) {
      this.rule = { ...this.rule, verification_method: value };
    }
  }

  get isMessengerSecuritySelected(): boolean {
    return this.rule?.verification_method === 'messenger_security';
  }

  get isMessengerSecurityChannelValid(): boolean {
    if (!this.isMessengerSecuritySelected) {
      return true;
    }
    if (this.isFallback) {
      return false;
    }
    return MESSENGER_SECURITY_CHANNELS.includes(this.rule?.channel || '');
  }

  get isMessengerSecurityAudienceValid(): boolean {
    if (!this.isMessengerSecuritySelected) {
      return true;
    }
    return this.rule?.audience === 'users_only';
  }

  get messengerSecurityValidationError(): string | null {
    if (!this.isMessengerSecuritySelected) {
      return null;
    }
    if (!this.isMessengerSecurityChannelValid || !this.isMessengerSecurityAudienceValid) {
      return this.intl.t(
        'new-settings.workspace.channel-identity-rules.validation.messenger-security-invalid',
      );
    }
    return null;
  }

  get isSaveDisabled(): boolean {
    if (!this.rule) {
      return true;
    }
    if (this.isNew && !this.isFallback && !this.rule.channel) {
      return true;
    }
    if (!this.rule.audience) {
      return true;
    }
    if (!this.isMessengerSecurityChannelValid || !this.isMessengerSecurityAudienceValid) {
      return true;
    }
    return false;
  }

  @dropTask
  *loadRule(id: number): TaskGenerator<void> {
    try {
      let appId = this.appService.app.id;
      let response: any = yield get(`/ember/channel_identity_rules/${id}?app_id=${appId}`);
      if (response.channel_identity_rule) {
        this.rule = response.channel_identity_rule;
      }
    } catch (_e: unknown) {
      this.notificationsService.notifyError(
        this.intl.t('new-settings.workspace.channel-identity-rules.notifications.load-error'),
      );
    }
  }

  @dropTask
  *save(): TaskGenerator<void> {
    if (!this.rule) {
      return;
    }

    try {
      let appId = this.appService.app.id;
      let payload = {
        channel: this.rule.fallback_rule ? null : this.rule.channel,
        audience: this.rule.audience,
        verification_method: this.rule.verification_method,
        fallback_rule: this.rule.fallback_rule,
      };

      if (this.isNew) {
        yield post(`/ember/channel_identity_rules?app_id=${appId}`, {
          channel_identity_rule: payload,
        });
        this.notificationsService.notifyConfirmation(
          this.intl.t('new-settings.workspace.channel-identity-rules.notifications.created'),
        );
      } else {
        yield put(`/ember/channel_identity_rules/${this.rule.id}?app_id=${appId}`, {
          channel_identity_rule: payload,
        });
        this.notificationsService.notifyConfirmation(
          this.intl.t('new-settings.workspace.channel-identity-rules.notifications.updated'),
        );
      }

      this.args.onSave();
    } catch (_e: unknown) {
      this.notificationsService.notifyError(
        this.intl.t(
          this.isNew
            ? 'new-settings.workspace.channel-identity-rules.notifications.create-error'
            : 'new-settings.workspace.channel-identity-rules.notifications.update-error',
        ),
      );
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::Workspace::ChannelIdentityRules::EditorModal': typeof EditorModal;
    'new-settings/workspace/channel-identity-rules/editor-modal': typeof EditorModal;
  }
}
